import axios from "axios";

/**
 * Upload a file directly to S3 via presigned URL, then register it with the backend.
 * The file payload never goes through the backend — only lightweight metadata calls.
 *
 * Flow: 1) Get presigned URL  2) PUT file to S3  3) Register file record + workflow association
 *
 * @param {File} file - The file to upload
 * @param {Object} [options]
 * @param {boolean} [options.isPublic=false] - Upload to public bucket
 * @param {Function} [options.onProgress] - Progress callback (0-100)
 * @returns {Promise<{url: string, key: string, bucket: string, region: string, permanentUrl: string, fileId: number}>}
 */
export async function uploadFile(file, options = {}) {
    const { isPublic = false, onProgress } = options;
    const apiKey = window.config?.apiKey;
    const proxyUrl = window.process?.env?.PROXY_INTEGRATION_URL;

    if (!proxyUrl) throw new Error("PROXY_INTEGRATION_URL is not configured");
    if (!file) throw new Error("No file provided");

    // Step 1: Get presigned upload URL
    const presignResponse = await axios.get(`${proxyUrl}/upload-url`, {
        params: {
            fileName: file.name,
            fileType: file.type,
            isPublic,
        },
        headers: { "x-api-key": apiKey },
    });

    const { url: presignedUrl, key, bucket, region, permanentUrl } = presignResponse.data;

    // Step 2: Upload file directly to S3
    // Uses fetch (not axios) to avoid interceptors adding headers that break the presigned signature.
    // Falls back to XMLHttpRequest when onProgress is provided, since fetch has no upload progress API.
    if (onProgress) {
        await new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open("PUT", presignedUrl);
            xhr.upload.onprogress = (e) => {
                if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
            };
            xhr.onload = () => xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`S3 upload failed: ${xhr.status} ${xhr.statusText}`));
            xhr.onerror = () => reject(new Error("S3 upload failed: network error"));
            xhr.send(file);
        });
    } else {
        const putResponse = await fetch(presignedUrl, { method: "PUT", body: file });
        if (!putResponse.ok) {
            throw new Error(`S3 upload failed: ${putResponse.status} ${putResponse.statusText}`);
        }
    }

    const url = permanentUrl || presignedUrl.split("?")[0];

    // Step 3: Register file record in DB and associate with workflow
    const completeResponse = await axios.post(`${proxyUrl}/upload-complete`, {
        key,
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        bucket,
        region,
        isPublic,
    }, {
        headers: { "x-api-key": apiKey },
    });

    return { url, key, bucket, region, permanentUrl, fileId: completeResponse.data.fileId };
}

/**
 * Get a signed download URL for a private file.
 *
 * @param {string} fileUrl - The S3 URL or key of the file
 * @returns {Promise<string>} The signed URL
 */
export async function getSignedUrl(fileUrl) {
    const apiKey = window.config?.apiKey;
    const proxyUrl = window.process?.env?.PROXY_INTEGRATION_URL;

    if (!proxyUrl) throw new Error("PROXY_INTEGRATION_URL is not configured");

    const response = await axios.get(`${proxyUrl}/files/signed-url`, {
        params: { fileUrl },
        headers: { "x-api-key": apiKey },
    });

    return response.data.signedUrl;
}
